import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Edit, Lock, LogOut, ArrowLeft, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

function ProfileManagement() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeSection, setActiveSection] = useState('view');
  const [passwordData, setPasswordData] = useState({ old: '', new: '', confirm: '' });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [langLoading, setLangLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserData();
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const res = await axios.get('/api/languages');
      setLanguages(res.data.languages || []);
    } catch (e) { console.error('Languages load error:', e); }
  };

  const fetchUserData = async () => {
    try {
      const [userRes, profileRes] = await Promise.all([
        axios.get('/api/auth/me'),
        axios.get('/api/profile')
      ]);
      setUser(userRes.data);
      if (profileRes.data.exists) {
        setProfile(profileRes.data.profile);
        setSelectedLanguage(profileRes.data.profile.preferred_language || 'English');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleLanguageChange = async (lang) => {
    setLangLoading(true);
    try {
      await axios.put('/api/profile/language', { preferred_language: lang });
      setSelectedLanguage(lang);
      setMessage(`Language updated to ${lang}. Your reports will regenerate in ${lang} on next view.`);
      setMessageType('success');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to update language');
      setMessageType('error');
    } finally {
      setLangLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/');
    window.location.reload();
  };

  const handleEditProfile = () => {
    if (profile && profile.edit_count >= 3) {
      setMessage('You have used all 3 profile edits. Contact support for assistance.');
      setMessageType('error');
      return;
    }
    navigate('/data-input');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!passwordData.old || !passwordData.new || !passwordData.confirm) {
      setMessage('Please fill in all password fields');
      setMessageType('error');
      return;
    }

    if (passwordData.new.length < 6) {
      setMessage('New password must be at least 6 characters');
      setMessageType('error');
      return;
    }

    if (passwordData.new !== passwordData.confirm) {
      setMessage('New passwords do not match');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/api/auth/change-password', {
        old_password: passwordData.old,
        new_password: passwordData.new
      });
      setMessage(res.data.message || 'Password changed successfully!');
      setMessageType('success');
      setPasswordData({ old: '', new: '', confirm: '' });
      setTimeout(() => setActiveSection('view'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to change password';
      setMessage(detail);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    background: '#0F1128',
    border: '1.5px solid #2D3154',
    borderRadius: '10px',
    color: '#E5E7EB',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s'
  };

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <button 
          data-testid="back-to-dashboard-btn"
          onClick={() => navigate('/dashboard')} 
          className="btn btn-ghost" 
          style={{ marginBottom: '2rem' }}
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '2rem' }}>Profile Management</h1>

          {message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="profile-message"
              style={{
                marginBottom: '2rem',
                padding: '1rem 1.5rem',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: messageType === 'success' ? 'rgba(16, 185, 129, 0.12)' : messageType === 'error' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                border: `1px solid ${messageType === 'success' ? '#10B981' : messageType === 'error' ? '#EF4444' : '#3B82F6'}`,
                color: messageType === 'success' ? '#10B981' : messageType === 'error' ? '#EF4444' : '#3B82F6'
              }}
            >
              {messageType === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
              {message}
            </motion.div>
          )}

          {/* Profile Overview */}
          {user && (
            <div className="card" style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #00D9FF 0%, #7C3AED 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#FFF'
                }}>
                  {user.full_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{user.full_name || 'User'}</h2>
                  <p style={{ color: '#9CA3AF' }}>{user.email}</p>
                </div>
              </div>

              {profile && (
                <div style={{ padding: '1.5rem', background: '#1E2139', borderRadius: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>Birth Date</p>
                      <p style={{ fontSize: '1rem', fontWeight: 600 }}>{profile.birth_date}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>Birth Time</p>
                      <p style={{ fontSize: '1rem', fontWeight: 600 }}>{profile.birth_time || 'Not set'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>Birth Place</p>
                      <p style={{ fontSize: '1rem', fontWeight: 600 }}>{profile.birth_place || 'Not set'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>Gender</p>
                      <p style={{ fontSize: '1rem', fontWeight: 600 }}>{profile.gender || 'Not set'}</p>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.875rem', color: '#3B82F6' }}>
                      Profile Edits Used: {profile.edit_count || 0} / 3 &bull; {3 - (profile.edit_count || 0)} edits remaining
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Language Preference */}
          {profile && (
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Report Language
              </h3>
              <p style={{ color: '#9CA3AF', fontSize: '0.88rem', marginBottom: '1rem' }}>
                All AI-generated reports (Life Predictions, Weekly Forecast, Numerology) will be written in your chosen language.
              </p>
              <div data-testid="language-selector" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {languages.map(lang => {
                  const isActive = selectedLanguage === lang.name;
                  return (
                    <button
                      key={lang.code}
                      data-testid={`lang-btn-${lang.code}`}
                      onClick={() => !isActive && handleLanguageChange(lang.name)}
                      disabled={langLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: isActive ? '2px solid #C9A961' : '1px solid rgba(255,255,255,0.15)',
                        background: isActive ? 'rgba(201,169,97,0.15)' : 'rgba(15,23,42,0.45)',
                        color: isActive ? '#C9A961' : '#D1D5DB',
                        cursor: langLoading ? 'wait' : 'pointer',
                        fontSize: '0.88rem',
                        fontWeight: isActive ? 700 : 400,
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: '90px',
                      }}
                    >
                      <span style={{ fontSize: '1.1rem', marginBottom: '0.15rem' }}>{lang.native}</span>
                      <span style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{lang.name}</span>
                    </button>
                  );
                })}
              </div>
              {selectedLanguage !== 'English' && (
                <p data-testid="language-active-note" style={{ color: '#C9A961', fontSize: '0.82rem', marginTop: '0.75rem' }}>
                  Active: Reports will be generated in {selectedLanguage}. Cached reports have been cleared — they will regenerate in {selectedLanguage} when you next open them.
                </p>
              )}
            </div>
          )}

          {/* Change Password Form */}
          <AnimatePresence>
            {activeSection === 'password' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="card"
                style={{ marginBottom: '2rem', overflow: 'hidden' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Lock size={24} color="#7C3AED" />
                    Change Password
                  </h3>
                  <button
                    data-testid="close-password-form-btn"
                    onClick={() => { setActiveSection('view'); setMessage(''); setPasswordData({ old: '', new: '', confirm: '' }); }}
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: 'none',
                      color: '#9CA3AF',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Cancel
                  </button>
                </div>

                <form onSubmit={handleChangePassword}>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>
                      Current Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        data-testid="old-password-input"
                        type={showOldPassword ? 'text' : 'password'}
                        value={passwordData.old}
                        onChange={(e) => setPasswordData({ ...passwordData, old: e.target.value })}
                        placeholder="Enter your current password"
                        style={inputStyle}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#9CA3AF',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>
                      New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        data-testid="new-password-input"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.new}
                        onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                        placeholder="Enter new password (min 6 characters)"
                        style={inputStyle}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#9CA3AF',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>
                      Confirm New Password
                    </label>
                    <input
                      data-testid="confirm-password-input"
                      type="password"
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                      placeholder="Re-enter new password"
                      style={inputStyle}
                    />
                    {passwordData.new && passwordData.confirm && passwordData.new !== passwordData.confirm && (
                      <p style={{ color: '#EF4444', fontSize: '0.8125rem', marginTop: '0.5rem' }}>Passwords do not match</p>
                    )}
                  </div>

                  <button
                    data-testid="change-password-submit-btn"
                    type="submit"
                    disabled={loading || !passwordData.old || !passwordData.new || !passwordData.confirm || passwordData.new !== passwordData.confirm}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: loading ? '#4B5563' : 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#FFF',
                      fontWeight: 600,
                      fontSize: '1rem',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: (!passwordData.old || !passwordData.new || !passwordData.confirm || passwordData.new !== passwordData.confirm) ? 0.5 : 1,
                      transition: 'opacity 0.2s, transform 0.2s'
                    }}
                  >
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action Cards */}
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="card"
              data-testid="edit-profile-card"
              onClick={handleEditProfile}
              style={{ cursor: 'pointer', padding: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                background: 'rgba(0, 217, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Edit size={28} color="#00D9FF" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Edit Profile</h3>
                <p style={{ fontSize: '0.9375rem', color: '#9CA3AF' }}>
                  Update your birth details and personal information
                </p>
              </div>
            </motion.div>

            {activeSection !== 'password' && (
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="card"
                data-testid="change-password-card"
                onClick={() => { setActiveSection('password'); setMessage(''); }}
                style={{ cursor: 'pointer', padding: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: 'rgba(124, 58, 237, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={28} color="#7C3AED" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Change Password</h3>
                  <p style={{ fontSize: '0.9375rem', color: '#9CA3AF' }}>
                    Update your account password for security
                  </p>
                </div>
              </motion.div>
            )}

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="card"
              data-testid="logout-card"
              onClick={handleLogout}
              style={{ cursor: 'pointer', padding: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem', border: '2px solid #DC2626' }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                background: 'rgba(220, 38, 38, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <LogOut size={28} color="#DC2626" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#DC2626' }}>Logout</h3>
                <p style={{ fontSize: '0.9375rem', color: '#9CA3AF' }}>
                  Sign out of your account
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ProfileManagement;
